
import { defineComponent, ref, reactive, onMounted } from "vue";
import { apiService } from "@services/api.service";
import { throttle } from "throttle-debounce";
import { Constants } from "@app/constants";
import { Emitter } from "@utils/events";
import { Loader, useLoader } from "@composables/loader";
import { useRoute } from "vue-router/composables";
import DirectMessageModal from "@components/messaging/direct-message.vue";
import CiButton from "@components/base/button.vue";
import CiContentTile from "@components/contents/ContentTile.vue";
import CiIsInViewport from "@directives/base/element-in-viewport";

export default defineComponent({
  name: "CiUsersProfileArticles",
  components: { "ci-direct-message-modal": DirectMessageModal, CiButton, CiContentTile },
  directives: { CiIsInViewport },
  props: {
    owner: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const communityManager = ref<UsersV2.Tile>();
    const loader = useLoader();
    const route = useRoute();
    const articles = ref<Articles.TileData[]>([]);
    const page = ref(1);
    const meta: NewsCenterApi.Meta = reactive({
      current_page: 0,
      next_page: 0,
      prev_page: 0,
      total_count: 0,
      total_pages: 0,
    });
    const loadUserArticles = async () => {
      loader.start();
      try {
        const response = await apiService.users.getUserArticles(route.params.slug, { page: page.value });
        meta.current_page = response.data.meta.current_page;
        meta.next_page = response.data.meta.next_page;
        meta.prev_page = response.data.meta.prev_page;
        meta.total_count = response.data.meta.total_count;
        meta.total_pages = response.data.meta.total_pages;
        articles.value = [...articles.value, ...response.data.contents];
      } catch (err) {
        articles.value.length = 0;
      } finally {
        loader.stop();
      }
    };

    const loadCommunityManager = async () => {
      loader.start();

      try {
        const { data } = await apiService.users.get(Constants.communityManagerSlug);

        communityManager.value = {
          avatar: data.images.avatar,
          company_name: data.company.name,
          id: data.id,
          name: data.display_name,
          position: data.position,
          url: `/members/${data.slug}`,
          verified: data.verified,
          considers_himself_professional: null,
          slug: data.slug,
        };
      } catch (error) {
        apiService.handleAPIError(error);
      } finally {
        loader.stop();
      }
    };

    onMounted(async () => {
      await loadUserArticles();
      await loadCommunityManager();
      Emitter.on(
        "ci::element-in-viewport",
        throttle(Constants.defaultDelay, (type) => {
          if (type === "article_lazyload" && meta.next_page) {
            page.value++;
            loadUserArticles();
          }
        })
      );
    });

    return {
      articles,
      communityManager,
      meta,
    };
  },
  computed: {
    loading(): boolean {
      return Loader.loading.state;
    },
    modalId(): string {
      return `modal-direct-message--${this.communityManager?.id}`;
    },
  },
});
